import { api } from "../../../../api/Middleware";
import {
  CONTACTS_URL,
  CONTACTS_SEARCH_URL,
  CONTACTS_BULK_MOVE_URL,
  CONTACTS_BULK_ASSIGN_URL,
  CONTACTS_BULK_TAG_URL,
  CONTACTS_BULK_DELETE_URL,
  CONTACTS_CHECK_DUPLICATES_URL,
  CONTACTS_EXPORT_URL,
  CONTACTS_IMPORT_URL,
  CONTACTS_REORDER_URL,
  CONTACTS_CRM_SUMMARY_URL,
  CONTACTS_TIMELINE_URL,
  CONTACTS_ACTIVITIES_URL,
  CONTACTS_TAGS_URL,
  CONTACTS_REMOVE_TAG_URL,
  CONTACTS_NOTES_URL,
  CONTACTS_DELETE_NOTE_URL,
  CONTACTS_PIN_NOTE_URL,
  CONTACTS_CONVERT_TO_CUSTOMER_URL,
  CONTACTS_TRANSFER_PIPELINE_URL,
} from "../../../../api/Urls";

// ========== CRUD ==========

export const getContacts = async (params = {}) => {
  const response = await api.get(CONTACTS_URL, { params });
  return response.data;
};

export const getContact = async (id) => {
  const response = await api.get(`${CONTACTS_URL}${id}/`);
  return response.data;
};

export const createContact = async (data) => {
  const response = await api.post(CONTACTS_URL, data);
  return response.data;
};

export const updateContact = async (id, data) => {
  const response = await api.patch(`${CONTACTS_URL}${id}/`, data);
  return response.data;
};

export const deleteContact = async (id) => {
  const response = await api.delete(`${CONTACTS_URL}${id}/`);
  return response.data;
};

export const getContactCRMSummary = async (id) => {
  const response = await api.get(CONTACTS_CRM_SUMMARY_URL(id));
  return response.data;
};

// ========== Pagination per column ==========

export const getContactsByColumn = async (columnId, offset = 0, limit = 10, extraParams = {}) => {
  const params = { column: columnId, offset, limit, view: 'board', ...extraParams };
  const response = await api.get(CONTACTS_URL, { params });
  return response.data;
};

// ========== Search ==========

export const searchContacts = async (query, pipelineId, limit = 20) => {
  const params = { q: query, limit };
  if (pipelineId) params.pipeline = pipelineId;
  const response = await api.get(CONTACTS_SEARCH_URL, { params });
  return response.data;
};

// ========== Reorder ==========

export const reorderContacts = async (items) => {
  const response = await api.post(CONTACTS_REORDER_URL, items);
  return response.data;
};

// ========== Bulk Operations ==========

export const bulkMoveContacts = async (ids, columnId) => {
  const response = await api.post(CONTACTS_BULK_MOVE_URL, { ids, column_id: columnId });
  return response.data;
};

export const bulkAssignContacts = async (ids, ownerId) => {
  const response = await api.post(CONTACTS_BULK_ASSIGN_URL, { ids, owner_id: ownerId });
  return response.data;
};

export const bulkTagContacts = async (ids, tagIds) => {
  const response = await api.post(CONTACTS_BULK_TAG_URL, { ids, tag_ids: tagIds });
  return response.data;
};

export const bulkDeleteContacts = async (ids) => {
  const response = await api.post(CONTACTS_BULK_DELETE_URL, { ids });
  return response.data;
};

// ========== Duplicates ==========

export const checkDuplicates = async (phone, email, excludeId) => {
  const data = {};
  if (phone) data.phone = phone;
  if (email) data.email = email;
  if (excludeId) data.exclude_id = excludeId;
  const response = await api.post(CONTACTS_CHECK_DUPLICATES_URL, data);
  return response.data;
};

// ========== Export / Import ==========

export const exportContacts = async (filters = {}, contactIds = []) => {
  const body = { filters };
  if (contactIds.length > 0) body.contact_ids = contactIds;

  const response = await api.post(CONTACTS_EXPORT_URL, body, {
    responseType: "blob",
  });

  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "contatos.csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const importContacts = async (file, pipelineId, columnId) => {
  const formData = new FormData();
  formData.append("file", file);
  if (pipelineId) formData.append("pipeline_id", pipelineId);
  formData.append("column_id", columnId);
  const response = await api.post(CONTACTS_IMPORT_URL, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

// ========== Timeline ==========

export const getTimeline = async (contactId, page = 1, pageSize = 20) => {
  const response = await api.get(CONTACTS_TIMELINE_URL(contactId), {
    params: { page, page_size: pageSize },
  });
  return response.data;
};

// ========== Contact Activities ==========

export const getContactActivities = async (contactId, status) => {
  const params = {};
  if (status) params.status = status;
  const response = await api.get(CONTACTS_ACTIVITIES_URL(contactId), { params });
  return response.data;
};

// ========== Tags ==========

export const getContactTags = async (contactId) => {
  const response = await api.get(CONTACTS_TAGS_URL(contactId));
  return response.data;
};

export const addContactTag = async (contactId, tagId) => {
  const response = await api.post(CONTACTS_TAGS_URL(contactId), { tag_id: tagId });
  return response.data;
};

export const removeContactTag = async (contactId, tagId) => {
  const response = await api.delete(CONTACTS_REMOVE_TAG_URL(contactId, tagId));
  return response.data;
};

// ========== Notes ==========

export const getContactNotes = async (contactId, type) => {
  const params = {};
  if (type) params.type = type;
  const response = await api.get(CONTACTS_NOTES_URL(contactId), { params });
  return response.data;
};

export const createContactNote = async (contactId, data) => {
  const response = await api.post(CONTACTS_NOTES_URL(contactId), data);
  return response.data;
};

export const deleteContactNote = async (contactId, noteId) => {
  const response = await api.delete(CONTACTS_DELETE_NOTE_URL(contactId, noteId));
  return response.data;
};

export const pinContactNote = async (contactId, noteId) => {
  const response = await api.patch(CONTACTS_PIN_NOTE_URL(contactId, noteId));
  return response.data;
};

// ========== Conversão Contact → Customer ==========

export const convertContactToCustomer = async (contactId, payload = {}) => {
  const response = await api.post(CONTACTS_CONVERT_TO_CUSTOMER_URL(contactId), payload);
  return response.data;
};

// ========== Transferência de Pipeline ==========

export const transferContactPipeline = async (contactId, pipelineId, columnId = null) => {
  const data = { pipeline_id: pipelineId };
  if (columnId) data.column_id = columnId;
  const response = await api.post(CONTACTS_TRANSFER_PIPELINE_URL(contactId), data);
  return response.data;
};
